import _typeof from "@babel/runtime/helpers/typeof";
import _extends from "@babel/runtime/helpers/extends";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _class;
function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }
function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }
import * as React from 'react';
import cn from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
import { clamp } from 'lodash-es';
import { OverlayStatusType } from './overlay.types';
import { safeInvoke, keys, isArray } from '../../../library';
import { Portal } from '../../portal';
export var overlayClasses = {
  MAIN: 'overlay',
  CONTENT: 'overlay__content',
  CONTAINER: 'overlay-container',
  BACKDROP: 'overlay__backdrop',
  IS_OPEN: '-open',
  IS_INLINE: '-inline',
  IS_SCROLLABLE: '-scrollable',
  IS_BLOCKING: '-blocking',
  BODY_OPEN: 'overlay-open'
};
export var Overlay = function (_React$Component) {
  _inherits(Overlay, _React$Component);
  var _super = _createSuper(Overlay);
  function Overlay(props) {
    var _this;
    _classCallCheck(this, Overlay);
    _this = _super.call(this, props);
    _defineProperty(_assertThisInitialized(_this), "containerRef", React.createRef());
    _defineProperty(_assertThisInitialized(_this), "bringFocusInsideOverlay", function () {
      return requestAnimationFrame(function () {
        if (_this.containerRef.current == null || document.activeElement == null || !_this.props.isOpen) {
          return;
        }
        var isFocusOutsideModal = !_this.containerRef.current.contains(document.activeElement);
        if (isFocusOutsideModal) {
          var autofocusElement = _this.containerRef.current.querySelector('[autofocus]');
          var wrapperElement = _this.containerRef.current.querySelector('[tabindex]');
          if (autofocusElement != null) {
            autofocusElement.focus();
          } else if (wrapperElement != null) {
            wrapperElement.focus();
          }
        }
      });
    });
    _defineProperty(_assertThisInitialized(_this), "overlayWillClose", function () {
      _this.teardown();
      _this.setState({
        status: OverlayStatusType.CLOSING
      }, function () {
        safeInvoke(_this.props.onClosing);
      });
      window.setTimeout(function () {
        _this.overlayIsClosed();
      }, _this.transition[1]);
    });
    _defineProperty(_assertThisInitialized(_this), "overlayIsClosed", function () {
      _this.setState({
        status: OverlayStatusType.CLOSED
      }, function () {
        safeInvoke(_this.props.onClosed);
      });
    });
    _defineProperty(_assertThisInitialized(_this), "overlayWillOpen", function () {
      var openStack = Overlay.openStack;
      if (openStack.length > 0) {
        document.removeEventListener("focus", Overlay.getLastOpened().handleDocumentFocus, true);
      }
      _this.setState({
        status: OverlayStatusType.OPENING
      }, function () {
        safeInvoke(_this.props.onOpening);
      });
      openStack.push(_assertThisInitialized(_this));
      if (_this.props.autoFocus) {
        _this.bringFocusInsideOverlay();
      }
      if (_this.props.enforceFocus) {
        document.addEventListener('focus', _this.handleDocumentFocus, true);
      }
      if (_this.props.isClosedOnOutsideClick && !_this.props.isBlocking) {
        document.addEventListener('mousedown', _this.handleDocumentClick);
      }
      if (_this.props.preventBodyScroll) {
        document.body.classList.add(overlayClasses.BODY_OPEN);
      }
      window.setTimeout(function () {
        _this.overlayIsOpened();
      }, _this.transition[0]);
    });
    _defineProperty(_assertThisInitialized(_this), "teardown", function () {
      var openStack = Overlay.openStack;
      var stackIndex = openStack.indexOf(_assertThisInitialized(_this));
      document.removeEventListener('focus', _this.handleDocumentFocus, true);
      document.removeEventListener('mousedown', _this.handleDocumentClick);
      if (stackIndex !== -1) {
        openStack.splice(stackIndex, 1);
        if (openStack.length > 0) {
          var lastOpenedOverlay = Overlay.getLastOpened();
          if (lastOpenedOverlay.props.enforceFocus) {
            document.addEventListener('focus', lastOpenedOverlay.handleDocumentFocus, true);
          }
        }
        if (Overlay.openStack.filter(function (o) {
          return o.props.preventBodyScroll;
        }).length === 0) {
          document.body.classList.remove(overlayClasses.BODY_OPEN);
        }
      }
    });
    _defineProperty(_assertThisInitialized(_this), "handleBackdropMouseDown", function (e) {
      var _this$props$backdropP;
      if (_this.props.isClosedOnOutsideClick) {
        safeInvoke(_this.props.onClose, e);
      }
      if (_this.props.enforceFocus) {
        _this.bringFocusInsideOverlay();
      }
      safeInvoke((_this$props$backdropP = _this.props.backdropProps) === null || _this$props$backdropP === void 0 ? void 0 : _this$props$backdropP.onMouseDown, e);
    });
    _defineProperty(_assertThisInitialized(_this), "handleKeyDown", function (e) {
      if (e.which === keys.ESCAPE && _this.props.isClosedOnEscape) {
        e.preventDefault();
        safeInvoke(_this.props.onClose, e);
      }
    });
    _defineProperty(_assertThisInitialized(_this), "handleDocumentClick", function (e) {
      var _this$props = _this.props,
        isClosedOnOutsideClick = _this$props.isClosedOnOutsideClick,
        isOpen = _this$props.isOpen,
        onClose = _this$props.onClose;
      var eventTarget = e.target;
      var stackIndex = Overlay.openStack.indexOf(_assertThisInitialized(_this));
      var isClickInThisOverlayOrDescendant = Overlay.openStack.slice(stackIndex).some(function (_ref) {
        var elem = _ref.containerRef.current;
        return elem && elem.contains(eventTarget) && !elem.isSameNode(eventTarget);
      });
      if (isOpen && isClosedOnOutsideClick && !isClickInThisOverlayOrDescendant) {
        safeInvoke(onClose, e);
      }
    });
    _defineProperty(_assertThisInitialized(_this), "handleDocumentFocus", function (e) {
      if (_this.props.enforceFocus && _this.containerRef.current != null && e.target instanceof Node && !_this.containerRef.current.contains(e.target)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        _this.bringFocusInsideOverlay();
      }
    });
    _defineProperty(_assertThisInitialized(_this), "maybeRenderBackdrop", function () {
      var _this$props$backdropP2;
      if (!_this.props.isOpen || !_this.props.hasBackdrop && !_this.props.isBlocking) return null;
      var enter = clamp(_this.transition[0] / 1000, 0, .35);
      var leave = clamp(_this.transition[1] / 1000, 0, .35);
      return React.createElement(motion.div, _extends({
        key: "backdrop"
      }, _this.props.backdropProps, {
        initial: {
          opacity: 0
        },
        animate: {
          opacity: _this.props.hasBackdrop ? 1 : 0,
          transition: {
            duration: enter
          }
        },
        exit: {
          opacity: 0,
          transition: {
            duration: leave,
            delay: _this.transition[1] / 1000 - leave
          }
        },
        className: cn(overlayClasses.BACKDROP, _this.props.backdropClassName, (_this$props$backdropP2 = _this.props.backdropProps) === null || _this$props$backdropP2 === void 0 ? void 0 : _this$props$backdropP2.className),
        onMouseDown: _this.handleBackdropMouseDown,
        tabIndex: _this.props.isClosedOnOutsideClick ? 0 : undefined
      }));
    });
    _defineProperty(_assertThisInitialized(_this), "maybeRenderChild", function (child) {
      if (child == null) {
        return null;
      }
      var decoratedChild = _typeof(child) === 'object' ? (React.cloneElement(child, {
        className: cn(child.props.className, overlayClasses.CONTENT),
        tabIndex: 0
      })) : React.createElement("span", {
        className: overlayClasses.CONTENT
      }, child);
      return decoratedChild;
    });
    _defineProperty(_assertThisInitialized(_this), "maybeRenderContainer", function () {
      if (_this.state.status === OverlayStatusType.CLOSED) return null;
      return React.Children.map(_this.props.children, _this.maybeRenderChild);
    });
    _defineProperty(_assertThisInitialized(_this), "renderOverlay", function () {
      var isBlocking = _this.props.isBlocking !== undefined ? _this.props.isBlocking : _this.props.hasBackdrop;
      var containerClasses = cn(overlayClasses.MAIN, "-".concat(_this.state.status), _defineProperty(_defineProperty({}, overlayClasses.IS_INLINE, !_this.props.usePortal), overlayClasses.IS_BLOCKING, isBlocking), _this.props.className);
      return React.createElement("div", {
        className: containerClasses,
        onKeyDown: _this.handleKeyDown,
        ref: _this.containerRef,
        style: _this.props.style
      }, React.createElement(AnimatePresence, null, _this.maybeRenderBackdrop()), _this.maybeRenderContainer());
    });
    _this.state = {
      hasEverOpened: props.isOpen,
      status: props.isOpen ? OverlayStatusType.OPEN : OverlayStatusType.CLOSED
    };
    return _this;
  }
  _createClass(Overlay, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      if (this.props.isOpen) {
        this.overlayWillOpen();
      }
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      if (prevProps.isOpen && !this.props.isOpen) {
        this.overlayWillClose();
      } else if (!prevProps.isOpen && this.props.isOpen) {
        this.overlayWillOpen();
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.teardown();
      safeInvoke(this.props.onClosed);
    }
  }, {
    key: "transition",
    get: function get() {
      return isArray(this.props.transitionDuration) ? this.props.transitionDuration : [this.props.transitionDuration, this.props.transitionDuration];
    }
  }, {
    key: "overlayIsOpened",
    value: function overlayIsOpened() {
      var _this2 = this;
      this.setState({
        status: OverlayStatusType.OPEN
      }, function () {
        safeInvoke(_this2.props.onOpened);
      });
    }
  }, {
    key: "render",
    value: function render() {
      if (this.props.isLazy && !this.state.hasEverOpened) {
        return null;
      }
      if (this.props.usePortal) {
        return React.createElement(Portal, {
          className: this.props.portalClassName,
          container: this.props.portalContainer
        }, this.renderOverlay());
      }
      return this.renderOverlay();
    }
  }], [{
    key: "getDerivedStateFromProps",
    value: function getDerivedStateFromProps(_ref2) {
      var hasEverOpened = _ref2.isOpen;
      if (hasEverOpened) {
        return {
          hasEverOpened: hasEverOpened
        };
      }
      return null;
    }
  }]);
  return Overlay;
}(React.Component);
_class = Overlay;
_defineProperty(Overlay, "defaultProps", {
  autoFocus: true,
  backdropProps: {},
  isClosedOnEscape: true,
  isClosedOnOutsideClick: true,
  hasBackdrop: false,
  isOpen: false,
  isLazy: true,
  transitionDuration: 0,
  usePortal: true
});
_defineProperty(Overlay, "openStack", []);
_defineProperty(Overlay, "getLastOpened", function () {
  return _class.openStack[_class.openStack.length - 1];
});